<template>
    <section
        v-if="swaps.length > 0"
        aria-labelledby="active-swaps-heading"
        class="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3"
        data-testid="active-swaps-banner"
    >
        <h3
            id="active-swaps-heading"
            class="text-sm font-medium uppercase tracking-[0.08em] text-amber-800"
        >
            Active swaps
        </h3>

        <ul class="space-y-3">
            <li
                v-for="swap in swaps"
                :key="swap.ulid"
                class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                data-testid="active-swap"
            >
                <p class="text-sm text-taupe-900">
                    <span class="font-medium">{{ sourceName(swap) }}</span>
                    <span class="text-taupe-500"> into </span>
                    <span class="font-medium">{{ targetName(swap) }}</span>
                    <span class="text-taupe-500">
                        · {{ flagCount(swap) }} ·
                        <template v-if="swap.expires_at">
                            ends
                            <zen-date-time :value="swap.expires_at" human />
                        </template>
                    </span>
                </p>

                <zen-button
                    variant="secondary"
                    :disabled="reverting.includes(swap.ulid)"
                    :aria-label="`Revert swap into ${targetName(swap)}`"
                    @click="$emit('revert', swap)"
                >
                    Revert
                </zen-button>
            </li>
        </ul>
    </section>
</template>

<script setup>
/**
 * Lists the temporary swaps still applying on the project's environments, each
 * with a one-click revert (ZEN-765). Names come from the environments the screen
 * already has, so the banner only needs the overlays and their names.
 */
const props = defineProps({
    // Overlays from the swap list endpoint, each with its target ULID.
    swaps: {
        type: Array,
        default: () => [],
    },
    // ULIDs of the overlays whose reverts are in flight. Each swap's button is disabled on its own.
    reverting: {
        type: Array,
        default: () => [],
    },
    // Environments on the screen, used to look up display names by ULID.
    environments: {
        type: Array,
        default: () => [],
    },
})

defineEmits(['revert'])

const environmentName = (ulid) =>
    props.environments.find((environment) => environment.ulid === ulid)?.name ??
    'Unknown environment'

const sourceName = (swap) => environmentName(swap.source_environment_ulid)

const targetName = (swap) => environmentName(swap.target_environment_ulid)

const flagCount = (swap) => {
    const count = swap.overrides?.length ?? 0
    return count === 1 ? '1 flag' : `${count} flags`
}
</script>
